import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/common/loading-button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useToast } from "@/components/ui/use-toast";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
// Removed unused imports
import { ErrorType, FormProps } from "@/types";
import { Product, ProductStatus, ProductType } from "@/types/system-setting/product";
import ProductSection from "./product-details";
import { useAddProductMutation, useUpdateProductMutation } from "@/services/productApi";
import { useGetCategoriesQuery } from "@/services/categoryApi";
import ProductPriceSection from "./price-details";
import { deleteCognitoImage, UploadCognitoImage } from "@/lib/utils/s3Service";
const { VITE_VIEW_S3_URL } = import.meta.env;

const ProductForm = ({
  isOpen,
  setOpen,
  data,
  setData,
  action,
  setAction,
  refetch,
}: FormProps<Product>) => {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;
  const { toast } = useToast();
  const [selectedImageFiles, setSelectedImageFiles] = useState<File[] | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const { data: productCategoriesData } = useGetCategoriesQuery({ query: "type=product", org_id: orgId });

  const productCategoryOptions = React.useMemo(() => {
    return productCategoriesData?.data?.map((cat: any) => ({
      value: cat.id,
      label: cat.name,
    })) || [];
  }, [productCategoriesData]);


  const initialProduct: Product = {
    org_id: orgId,
    sku: "",
    product_name: "",
    vendor: "",
    description: "",
    price: undefined,
    cost: undefined,
    price_master_id: undefined,
    type: ProductType.OtherProduct,
    manage_inventory: false,
    quantity: undefined,
    income_category_id: undefined,
    tax_group_id: undefined,
    status: ProductStatus.ACTIVE,
    product_category_id: undefined ,
    flexible_price: false,
    image_url: ""
  };

  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const form = useForm<Product>({
    mode: "all",
    defaultValues: initialProduct,
  });
  const [
    addProduct,
    {
      isLoading: addProductLoading,
      error: productAddError,
      isError: productAddErrorBool,
    },
  ] = useAddProductMutation();
  const [
    updateProduct,
    {
      isLoading: updateProductLoading,
      error: productUpdateError,
      isError: productUpdateErrorBool,
    },
  ] = useUpdateProductMutation();
  const {
    control,
    watch,
    register,
    handleSubmit,
    clearErrors,
    reset,
    setValue,
    getValues,
    trigger,
    formState: { isSubmitting, errors },
  } = form;

  const watcher = watch();

  useEffect(() => {
    if (action == "edit") {
    } else if (action == "add") {
      // need to add initial value here
      reset(initialProduct, {
        keepIsSubmitted: false,
        keepSubmitCount: false,
        keepDirtyValues: false,
        keepDefaultValues: true,
      });
    }
  }, [action, reset]);

  const handleClose = () => {
    clearErrors();
    setAction("add");
    // Clean up preview URLs
    previewUrls.forEach(url => URL.revokeObjectURL(url));
    setSelectedImageFiles(null);
    setPreviewUrls([]);
    reset(initialProduct, {
      keepIsSubmitted: false,
      keepSubmitCount: false,
      keepDirtyValues: false,
      keepDefaultValues: true,
    });
    setOpen(false);
  };
  useEffect(() => {
    if (action == "edit" && data) {
      console.log({ data }, "edit");
      reset(data as Product);
    } else if (action == "add" && data == undefined) {
      console.log({ initialProduct }, "add");
      reset(initialProduct, {
        keepIsSubmitted: false,
        keepSubmitCount: false,
      });
    }
  }, [action, data, reset]);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      previewUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);

  const onError = () => {
    toast({
      variant: "destructive",
      description: "Please fill all the mandatory fields",
    });
  };

  const onSubmit = async (data: Product) => {
    let payload: Product;

    if (orgId) {
      payload = {
        ...data,
        product_name: data.product_name.trim(),
        description:
          data?.description?.trim() === ""
            ? ""
            : data?.description,
        price: data.price ? data.price : 0,
        price_master_id: data.flexible_price ? undefined : data.price_master_id,
        cost: data.type === ProductType.Wallet ? 0 : data.cost
      };

      if (selectedImageFiles && selectedImageFiles.length > 0) {
        try {
          const selectedImage = selectedImageFiles[0];
          if (data.image_url !== "" && data.image_url) {
            await deleteCognitoImage(data.image_url as string);
          }
          const getUrl = await UploadCognitoImage(selectedImage);
          payload = {
            ...payload,
            image_url: getUrl?.location as string,
          };
        } catch (error) {
          console.error("Upload failed:", error);
          toast({
            variant: "destructive",
            title: "Image Upload Failed",
            description: "Please try again.",
          });
          return;
        }
      }
      try {
        if (action === "add") {
          const resp = await addProduct(payload).unwrap();
          if (resp) {
            toast({
              variant: "success",
              title: "Product saved successfully",
            });
            refetch();
            handleClose();
          }
        } else if (action === "edit") {
          const resp = await updateProduct({
            ...payload,
            id: payload.id
          }).unwrap();
          if (resp) {
            toast({
              variant: "success",
              title: "Product updated successfully",
            });
            refetch();
            handleClose();
          }
        }
      } catch (error) {
        console.error("Error", { error });
        if (error && typeof error === "object" && "data" in error) {
          const typedError = error as ErrorType;
          toast({
            variant: "destructive",
            title: "Error in Submission",
            description: typedError.data?.detail ?? typedError.data?.message,
          });
        } else {
          toast({
            variant: "destructive",
            title: "Error in Submission",
            description: `${error}||Something Went Wrong.`,
          });
        }
      }
    }
  };

  const handleFileUpload = (files: File[] | null) => {
    // Clean up previous preview URLs
    previewUrls.forEach(url => URL.revokeObjectURL(url));

    if (files && files.length > 0) {
      setSelectedImageFiles(files);
      // Create preview URLs for new files
      const urls = files.map(file => URL.createObjectURL(file));
      setPreviewUrls(urls);
    } else {
      setSelectedImageFiles(null);
      setPreviewUrls([]);
    }
  };

  const handleRemoveImage = async () => {
    // Clean up preview URLs
    previewUrls.forEach(url => URL.revokeObjectURL(url));

    const currentImageUrl = watcher.image_url;
    if (currentImageUrl) {
      try {
        await deleteCognitoImage(currentImageUrl as string);
      } catch (error) {
        console.error("Error deleting image from S3:", error);
      }
    }
    setSelectedImageFiles(null);
    setPreviewUrls([]);
    setValue("image_url", "");
  };


  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        hideCloseButton
        className="bg-formbg w-full p-0 custom-scrollbar h-full lg:!max-w-[1000px]"
      >
        <FormProvider {...form}>
          <form
            noValidate
            className="pb-4 w-full"
            onSubmit={handleSubmit(onSubmit, onError)} // need to add on submit here
            key={action}
          >
            <SheetHeader className="px-5 sticky top-0 z-40 py-2 bg-white shadow-sm">
              <SheetTitle >
                <div className="flex justify-between gap-5 items-start  ">
                  <div className="flex items-start gap-3">
                    <button type="button" onClick={handleClose}>
                      <i className="fa fa-xmark text-xl"></i>
                    </button>
                    <h1 className="font-medium">{action == "add" ? "Create" : "Edit"} Product</h1>
                  </div>

                  <div className="flex justify-center gap-4">
                    <Button
                      type="button"
                      className="w-[100px] text-center flex items-center gap-2 border-primary"
                      variant={"outline"}
                      onClick={handleClose}
                    >
                      <i className="fa fa-xmark "></i>
                      Cancel
                    </Button>

                    <LoadingButton
                      type="submit"
                      className="w-[100px] bg-primary text-black text-center flex items-center gap-2"
                      loading={isSubmitting}
                      disabled={isSubmitting}
                    >
                      {!isSubmitting && (
                        <i className="fa-regular fa-floppy-disk text-base px-1 "></i>
                      )}
                      {data ? "Update" : "Save"}
                    </LoadingButton>
                  </div>
                </div>
              </SheetTitle>
            </SheetHeader>
            <div className="h-[100%] mt-4 flex flex-col items-center gap-4 overflow-y-auto  pb-6">
              <ProductSection
                control={control}
                errors={errors}
                watch={watch}
                selectedImageFiles={selectedImageFiles}
                previewUrls={previewUrls}
                isUploadingImage={isUploadingImage}
                onFileUpload={handleFileUpload}
                onRemoveImage={handleRemoveImage}
                VITE_VIEW_S3_URL={VITE_VIEW_S3_URL}
              />
              <div className="w-[95%]">
                <ProductPriceSection
                  control={control}
                  errors={errors}
                  watch={watch}
                  setValue={setValue}
                  getValues={getValues}
                  register={register}
                  trigger={trigger}
                />
              </div>
            </div>
          </form>
        </FormProvider>
      </SheetContent>
    </Sheet>
  );
};

export default ProductForm;
